import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { useVideoCall, CallStatus } from '../context/VideoCallContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageLoader from '../components/PageLoader';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, 
  Mic, MicOff, FileText, Play, Clock, User, ChevronDown, ChevronUp, X, PhoneCall,
  Video, VideoOff, Users, Search, Star, Maximize2, Minimize2, Loader2, UserPlus, ArrowRightLeft
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { useTwilioNotifications } from '../context/TwilioNotificationContext';
import { useTwilioVoice } from '../context/TwilioVoiceContext';
import { parseServerDate } from '../utils/dateUtils';
import DiarizedTranscript from '../components/DiarizedTranscript';
import { usePageHeader } from '../context/PageHeaderContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

// Test recording URL for admin calls
// (Removed legacy TEST_RECORDING_URL — Play Recording now streams the real
// recording through the authenticated backend proxy.)

// Map US state abbreviations to IANA timezones. The History tab renders
// every call timestamp in the LOGGED-IN USER's home-state timezone so
// that an Eastern teammate seeing a call from a Texas teammate doesn't
// silently shift to the local browser timezone (was showing 8:28pm
// when the actual call was earlier — user-reported bug).
const STATE_TIMEZONES = {
  AL: 'America/Chicago',     AK: 'America/Anchorage',  AZ: 'America/Phoenix',
  AR: 'America/Chicago',     CA: 'America/Los_Angeles', CO: 'America/Denver',
  CT: 'America/New_York',    DE: 'America/New_York',   FL: 'America/New_York',
  GA: 'America/New_York',    HI: 'Pacific/Honolulu',   ID: 'America/Boise',
  IL: 'America/Chicago',     IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',     KS: 'America/Chicago',    KY: 'America/New_York',
  LA: 'America/Chicago',     ME: 'America/New_York',   MD: 'America/New_York',
  MA: 'America/New_York',    MI: 'America/Detroit',    MN: 'America/Chicago',
  MS: 'America/Chicago',     MO: 'America/Chicago',    MT: 'America/Denver',
  NE: 'America/Chicago',     NV: 'America/Los_Angeles', NH: 'America/New_York',
  NJ: 'America/New_York',    NM: 'America/Denver',     NY: 'America/New_York',
  NC: 'America/New_York',    ND: 'America/Chicago',    OH: 'America/New_York',
  OK: 'America/Chicago',     OR: 'America/Los_Angeles', PA: 'America/New_York',
  RI: 'America/New_York',    SC: 'America/New_York',   SD: 'America/Chicago',
  TN: 'America/Chicago',     TX: 'America/Chicago',    UT: 'America/Denver',
  VT: 'America/New_York',    VA: 'America/New_York',   WA: 'America/Los_Angeles',
  WV: 'America/New_York',    WI: 'America/Chicago',    WY: 'America/Denver',
  DC: 'America/New_York',
};

const formatCallTimestamp = (raw, stateAbbr) => {
  if (!raw) return '';
  const d = parseServerDate(raw);
  if (!d || isNaN(d.getTime())) return '';
  const tz = STATE_TIMEZONES[(stateAbbr || '').toUpperCase()] || undefined;
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: tz,
      timeZoneName: 'short',
    }).format(d);
  } catch (e) {
    return d.toLocaleString();
  }
};

const CallsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user, isFeatureEnabled } = useAuth();
  const { playSong } = useMusicPlayer();
  const { 
    callState, 
    mediaState, 
    initiateCall, 
    endCall, 
    cancelCall,
    toggleMic, 
    toggleCamera, 
    toggleMinimize,
    formatDuration: formatCallDuration,
    isInCall
  } = useVideoCall();
  const twilioNotifications = useTwilioNotifications();
  const twilioVoice = useTwilioVoice();
  
  const [activeTab, setActiveTab] = useState('external');
  const [callHistory, setCallHistory] = useState([]);
  const [videoCallHistory, setVideoCallHistory] = useState([]);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [callTypeFilter, setCallTypeFilter] = useState(null);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [transcriptEnabled, setTranscriptEnabled] = useState(true);
  // iter156 — Admin/special users have a per-user default for whether
  // outgoing calls are recorded. The keypad toggle below is initialized
  // from `defaultRecordOutgoing` on mount + RESET back to it after every
  // placeCall completes. So an admin who defaults to OFF can flip ON
  // for one call; returning to the keypad shows OFF again. Inverse for
  // a default of ON.
  const [defaultRecordOutgoing, setDefaultRecordOutgoing] = useState(true);
  // iter157 — Bottom-sheet picker that lists this user's external
  // contacts. Tap a contact's call icon to immediately dial their
  // saved phone number through the keypad's existing placeCall path
  // (which respects the per-call record toggle + role-locks).
  // iter158 — Per-user "star" pins favorites to the top of the list +
  // an in-sheet search bar filters by name OR phone digits.
  const [externalContactsSheetOpen, setExternalContactsSheetOpen] = useState(false);
  const [externalContacts, setExternalContacts] = useState([]);
  const [externalContactsLoading, setExternalContactsLoading] = useState(false);
  const [externalContactDialing, setExternalContactDialing] = useState(null);
  const [externalContactsQuery, setExternalContactsQuery] = useState('');
  const [starPending, setStarPending] = useState({}); // {contactId: true} while a toggle round-trip is in flight
  const [dialedNumber, setDialedNumber] = useState('');
  // Persists the last successfully-dialed PSTN number so an empty-input
  // tap on the call button retypes it (iOS-style "redial"). It is
  // populated AFTER a successful placeCall (right before clearing the
  // input), and survives page navigation via localStorage.
  const [lastDialed, setLastDialed] = useState(() => {
    try { return localStorage.getItem('calls.lastDialed') || ''; } catch (e) { return ''; }
  });
  const [loading, setLoading] = useState(false);
  
  // Video call state
  const [onboardedUsers, setOnboardedUsers] = useState([]);
  const [videoSearchQuery, setVideoSearchQuery] = useState('');
  const [isVideoInitiating, setIsVideoInitiating] = useState(false);
  
  // External/Internal call mode
  const [callMode, setCallMode] = useState('external'); // 'external' or 'internal'
  const [internalContacts, setInternalContacts] = useState([]);
  const [internalContactsLoading, setInternalContactsLoading] = useState(true);
  const [externalCallLoading, setExternalCallLoading] = useState(false);

  // Check URL params for initial tab. Default to "external" — the
  // restructured calls page no longer has a single "Calls" tab; the
  // External/Internal sub-toggle has been promoted to top-level tabs.
  // iter123 — Video tab merged into Internal (the Internal contact
  // cards now expose both Phone + Video buttons). Legacy ?tab=video
  // links land on Internal instead of a dead tab.
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'video') {
      setActiveTab('internal');
    } else if (tabParam === 'internal') {
      setActiveTab('internal');
      setCallMode('internal');
    } else if (tabParam === 'external' || tabParam === 'call') {
      setActiveTab('external');
      setCallMode('external');
    } else if (tabParam === 'history') {
      // History is now a separate page.
      navigate('/calls/history', { replace: true });
    }
  }, [searchParams, navigate]);

  // Deep-link from a Service Worker push action (Decline button on incoming-call notif)
  useEffect(() => {
    const declineId = searchParams.get('decline');
    if (declineId) {
      api.post(`/video-calls/${declineId}/decline`, { reason: 'declined_from_notification' })
        .catch(() => { /* call may already be gone */ });
    }
    // ?incoming=<id> is a no-op here — VideoCallContext's polling picks up
    // any active incoming call and pops the IncomingCallModal automatically.
  }, [searchParams, api]);

  // Deep-link from a Service Worker push action — PSTN incoming.
  // The push notification's "Answer" action is handled app-wide by
  // <IncomingCallConnectingOverlay/> (mounted at App root) so it
  // works regardless of which page the SW lands on. We only handle
  // the Decline path here because it's tied to /calls history.
  const pstnDeepLinkRef = React.useRef({ decline: null });
  const { rejectIncoming } = useTwilioVoice() || {};
  useEffect(() => {
    const declineSid = searchParams.get('pstn_decline');
    if (declineSid && pstnDeepLinkRef.current.decline !== declineSid) {
      pstnDeepLinkRef.current.decline = declineSid;
      // Two-pronged: if our SDK has already received the call,
      // reject it locally; AND tell the backend to end the parked
      // conference so the caller falls through to VM immediately.
      try { rejectIncoming?.(); } catch (e) { /* ignore */ }
      api.post(`/twilio/calls/${declineSid}/decline-incoming`, {})
        .catch(() => { /* call may already be gone */ });
      toast.message('Call declined');
    }
  }, [searchParams, api, rejectIncoming]);

  // Deep-link from Contacts: /calls?call=<userId>&mode=voice|video → start the call
  const triggeredCallRef = React.useRef(null);
  useEffect(() => {
    const calleeId = searchParams.get('call');
    const mode = searchParams.get('mode') === 'voice' ? 'voice' : 'video';
    if (!calleeId || triggeredCallRef.current === calleeId) return;
    triggeredCallRef.current = calleeId;
    (async () => {
      try {
        await initiateCall(calleeId, mode);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not start call');
      }
    })();
  }, [searchParams, initiateCall]);

  // Auto-switch to Internal tab when in a call so the active call UI
  // (which lives under Internal as of iter123) is visible.
  useEffect(() => {
    if (isInCall && activeTab !== 'internal') {
      setActiveTab('internal');
    }
  }, [isInCall, activeTab]);

  useEffect(() => {
    fetchCallHistory();
    fetchOnboardedUsers();
    fetchVideoCallHistory();
    fetchInternalContacts();
    // iter156 — pull per-user call-recording defaults and seed the
    // keypad toggle from `default_record_outgoing`. Endpoint returns
    // {default_record_outgoing, default_record_incoming, can_edit};
    // every role gets the GET, only admin+special can edit (handled
    // in the Settings page).
    (async () => {
      try {
        const r = await api.get('/users/me/call-recording-defaults');
        const out = r.data?.default_record_outgoing;
        if (typeof out === 'boolean') {
          setDefaultRecordOutgoing(out);
          setRecordingEnabled(out);
          setTranscriptEnabled(out);
        }
      } catch (e) { /* leave defaults */ }
    })();
  }, []);

  const fetchCallHistory = async () => {
    try {
      const res = await api.get('/calls');
      setCallHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    }
  };

  const fetchVideoCallHistory = async () => {
    try {
      const res = await api.get('/video-calls/history/list');
      setVideoCallHistory(res.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch video call history:', error);
    }
  };

  const fetchOnboardedUsers = async () => {
    try {
      const res = await api.get('/users/all');
      setOnboardedUsers(res.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchInternalContacts = async () => {
    setInternalContactsLoading(true);
    try {
      const res = await api.get('/contacts');
      const internal = res.data?.internal || [];
      setInternalContacts(internal);
    } catch (error) {
      console.error('Failed to fetch internal contacts:', error);
    } finally {
      setInternalContactsLoading(false);
    }
  };

  const handleDial = (digit) => {
    if (dialedNumber.length < 15) {
      setDialedNumber(prev => prev + digit);
    }
  };

  const handleDelete = () => {
    setDialedNumber(prev => prev.slice(0, -1));
  };

  const handleCall = async () => {
    // Empty-input redial: clicking the call button with no digits
    // retypes the most recently dialed number into the input. The
    // user must press the call button AGAIN to actually place the
    // call — matches the native iPhone Phone app's redial UX.
    if (!dialedNumber) {
      if (lastDialed) {
        setDialedNumber(lastDialed);
      } else {
        toast.error('No previous number to redial');
      }
      return;
    }
    if (dialedNumber.length < 3) {
      toast.error('Please enter a valid number');
      return;
    }
    if (!twilioVoice?.deviceState?.ready) {
      toast.error('Phone is still connecting — try again in a few seconds.');
      return;
    }
    setExternalCallLoading(true);
    try {
      // The Twilio Voice SDK handles the WebRTC handshake — your browser
      // becomes a real audio leg. Twilio fetches /api/twilio/voice/outgoing
      // for the TwiML, which bridges to the external number using the state's
      // configured business number as caller ID.
      const call = await twilioVoice.placeCall(dialedNumber, {
        state: user?.state,
        contactName: dialedNumber,
        // Pass the current toggle state — when false the backend
        // skips recording, the call-start disclaimer, and the AI
        // summary/transcript pipeline for this call.
        record: recordingEnabled && transcriptEnabled,
      });
      // Surface the call in the existing in-call UI.
      if (twilioNotifications?.setOutboundCall) {
        twilioNotifications.setOutboundCall({
          // Real Twilio CallSid is patched in once the SDK fires 'accept'.
          // Until then, hold/transfer/add buttons are disabled — they need a
          // real SID to hit the backend REST endpoints.
          call_sid: `sdk:${Date.now()}`,
          call_id: null,
          to_phone: dialedNumber,
          contact_phone: dialedNumber,
          direction: 'outbound',
          _sdkCall: call,
        });
        // Patch the real Twilio CallSid as soon as the bridge connects.
        try {
          call.on('accept', () => {
            const realSid = call.parameters?.CallSid;
            if (realSid && twilioNotifications?.patchActiveCall) {
              twilioNotifications.patchActiveCall({ call_sid: realSid });
            }
          });
        } catch (e) { /* ignore */ }
      }
      // Remember this number for next time's redial. We persist
      // BEFORE clearing the input so the value survives even if the
      // component unmounts mid-render.
      setLastDialed(dialedNumber);
      try { localStorage.setItem('calls.lastDialed', dialedNumber); } catch (e) { /* ignore */ }
      setDialedNumber('');
      fetchCallHistory();
    } catch (error) {
      const detail = error?.message || error?.response?.data?.detail || 'Failed to place call';
      toast.error(detail);
    } finally {
      setExternalCallLoading(false);
      // iter156 — Per-call override is one-shot. Reset the keypad
      // toggle back to the user's default so the next call starts
      // from the stored preference (OFF for admins who default off,
      // ON otherwise).
      setRecordingEnabled(defaultRecordOutgoing);
      setTranscriptEnabled(defaultRecordOutgoing);
    }
  };

  const handleInternalVoiceCall = async (targetUser) => {
    try {
      setIsVideoInitiating(true);
      await initiateCall(targetUser.id, 'voice');
      toast.success(`Calling ${getFullName(targetUser)}...`);
    } catch (error) {
      console.error('Failed to start voice call:', error);
      toast.error(error.response?.data?.detail || 'Failed to start call');
    } finally {
      setIsVideoInitiating(false);
    }
  };

  // iter157 — Lazy-fetch the caller's external contact list on the
  // first open of the "Call from Contact List?" sheet so we don't
  // burn a round-trip every Calls render. The bottom sheet shows
  // each contact in a thread-card style with a call icon button.
  const openExternalContactsSheet = async () => {
    setExternalContactsSheetOpen(true);
    if (externalContacts.length > 0) return;
    setExternalContactsLoading(true);
    try {
      const res = await api.get('/twilio/contacts/my-external');
      const contacts = (res.data?.contacts || []).filter(
        (c) => (c.phone_number || '').trim()
      );
      setExternalContacts(contacts);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load external contacts');
    } finally {
      setExternalContactsLoading(false);
    }
  };

  // iter158 — Toggle star (per-user). Optimistic update + server call;
  // revert on failure so the UI never goes out of sync with the DB.
  const toggleStarContact = async (contact) => {
    const id = contact.id;
    const newStarred = !contact.starred;
    setStarPending((p) => ({ ...p, [id]: true }));
    // Optimistic
    setExternalContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, starred: newStarred } : c))
    );
    try {
      if (newStarred) {
        await api.post(`/twilio/contacts/${id}/star`);
      } else {
        await api.delete(`/twilio/contacts/${id}/star`);
      }
    } catch (err) {
      // Revert
      setExternalContacts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, starred: !newStarred } : c))
      );
      toast.error(err?.response?.data?.detail || 'Could not update favorite');
    } finally {
      setStarPending((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const callExternalContact = async (contact) => {
    const phone = (contact?.phone_number || '').trim();
    if (!phone) {
      toast.error('No phone number saved for this contact.');
      return;
    }
    if (!twilioVoice?.deviceState?.ready) {
      toast.error('Phone is still connecting — try again in a few seconds.');
      return;
    }
    setExternalContactDialing(contact.id);
    try {
      const call = await twilioVoice.placeCall(phone, {
        state: user?.state,
        contactName: contact.name || phone,
        // Honour the keypad toggle. The Settings default is already
        // baked into recordingEnabled at this point (mount-time fetch
        // + per-call reset after each placeCall).
        record: recordingEnabled && transcriptEnabled,
      });
      if (twilioNotifications?.setOutboundCall) {
        twilioNotifications.setOutboundCall({
          call_sid: `sdk:${Date.now()}`,
          call_id: null,
          to_phone: phone,
          contact_phone: phone,
          contact_id: contact.id,
          contact_name: contact.name,
          direction: 'outbound',
          _sdkCall: call,
        });
        try {
          call.on('accept', () => {
            const realSid = call.parameters?.CallSid;
            if (realSid && twilioNotifications?.patchActiveCall) {
              twilioNotifications.patchActiveCall({ call_sid: realSid });
            }
          });
        } catch (e) { /* ignore */ }
      }
      setLastDialed(phone);
      try { localStorage.setItem('calls.lastDialed', phone); } catch (e) { /* ignore */ }
      setExternalContactsSheetOpen(false);
      fetchCallHistory();
    } catch (error) {
      const detail = error?.message || error?.response?.data?.detail || 'Failed to place call';
      toast.error(detail);
    } finally {
      setExternalContactDialing(null);
      // Same per-call override reset as handleCall.
      setRecordingEnabled(defaultRecordOutgoing);
      setTranscriptEnabled(defaultRecordOutgoing);
    }
  };

  const dialPadButtons = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' }
  ];

  const getCallIcon = (type) => {
    switch (type) {
      case 'inbound': return <PhoneIncoming className="w-4 h-4 text-green-500" />;
      case 'outbound': return <PhoneOutgoing className="w-4 h-4 text-primary" />;
      case 'missed': return <PhoneMissed className="w-4 h-4 text-red-500" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayRecording = async (call) => {
    // Fetch the recording with the auth token, convert to a blob URL, and
    // hand THAT to the music player. Setting `<audio src="...api/twilio/
    // recording/.../audio">` directly does NOT work — the browser does
    // not include the `Authorization: Bearer …` header on media element
    // requests, so the proxy returns 401/403 and the audio element
    // silently fails (no error, no playback). The blob URL works because
    // it's a data URL local to the browser.
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    let fetchUrl = null;
    let isInternalDirect = false;
    if (call._kind === 'internal') {
      const ref = call.original?.recording_url || call.recording_url;
      if (ref && ref !== '__INTERNAL__') {
        fetchUrl = ref.startsWith('http') ? ref : `${apiBase}${ref}`;
        isInternalDirect = true; // already publicly served from our /media/...
      }
    } else if (call.recording_url) {
      fetchUrl = `${apiBase}/api/twilio/recording/${call.id}/audio`;
    }

    if (!fetchUrl) {
      toast.message('Recording not available yet — it usually appears within a minute of hangup.');
      return;
    }

    try {
      const headers = isInternalDirect ? {} : { Authorization: `Bearer ${localStorage.getItem('orb_token') || ''}` };
      const resp = await fetch(fetchUrl, { headers });
      if (!resp.ok) {
        toast.error(`Recording failed to load (HTTP ${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const recordingSong = {
        id: `recording-${call.id}`,
        title: 'Call Recording',
        subtitle: call.phone_number,
        artist: call.call_type.charAt(0).toUpperCase() + call.call_type.slice(1),
        url: blobUrl,
      };
      playSong(recordingSong, 'recording');
      toast.success('Playing recording');
    } catch (e) {
      console.error('[Recording] fetch failed:', e);
      toast.error(e?.message || 'Could not load recording');
    }
  };

  const handleShowTranscript = (call) => {
    setSelectedTranscript(call);
    setShowTranscriptDialog(true);
  };

  // Video call functions - now using real WebRTC
  const handleStartVideoCall = async (targetUser) => {
    try {
      setIsVideoInitiating(true);
      await initiateCall(targetUser.id, 'video');
      toast.success(`Calling ${getFullName(targetUser)}...`);
    } catch (error) {
      console.error('Failed to start video call:', error);
      toast.error(error.response?.data?.detail || 'Failed to start call');
    } finally {
      setIsVideoInitiating(false);
    }
  };

  const handleEndVideoCall = async () => {
    try {
      if (callState.status === CallStatus.CALLING && callState.isCaller) {
        await cancelCall();
      } else {
        await endCall();
      }
      toast.info('Video call ended');
      // Refresh history
      fetchVideoCallHistory();
    } catch (error) {
      console.error('Failed to end call:', error);
    }
  };

  const formatVideoDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    }
    if (u?.full_name) {
      return u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name} ${u.last_name}`;
    }
    return u?.full_name || 'Unknown User';
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 border-slate-500/20';
    }
  };

  // Filter users based on search
  const filteredVideoUsers = videoSearchQuery.length >= 2 
    ? onboardedUsers.filter(u => getFullName(u).toLowerCase().includes(videoSearchQuery.toLowerCase()))
    : [];

  // Header overrides — move History / Unassigned / Contacts icons up
  // into the top bar next to the bell. Per-feature flag gating still
  // applies; missing perms simply omit the icon.
  const showHistory = isFeatureEnabled('calls_history');
  const showUnassigned = ['admin', 'supervisor', 'lead'].includes(user?.role);
  const showContacts = isFeatureEnabled('calls_contacts');
  usePageHeader({
    title: 'Calls',
    rightSlot: (
      <>
        {showHistory && (
          <button
            onClick={() => navigate('/calls/history')}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="History"
            data-testid="header-history-btn"
          >
            <Clock className="w-5 h-5" />
          </button>
        )}
        {showUnassigned && (
          <button
            onClick={() => navigate('/unassigned-callers')}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="Unassigned callers"
            data-testid="header-unassigned-btn"
          >
            <UserPlus className="w-5 h-5" />
          </button>
        )}
        {showContacts && (
          <button
            onClick={() => navigate('/contacts')}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="Contacts"
            data-testid="header-contacts-btn"
          >
            <Users className="w-5 h-5" />
          </button>
        )}
      </>
    ),
  }, [showHistory, showUnassigned, showContacts]);

  // Pull-to-refresh — scroll lives on the page container itself
  // (fixed inset-x-0 overflow-y-auto) not the document.
  const scrollerRef = useRef(null);
  const refreshAll = async () => {
    await Promise.allSettled([
      fetchCallHistory(),
      fetchOnboardedUsers(),
      fetchVideoCallHistory(),
      fetchInternalContacts(),
    ]);
  };
  const { pullY, refreshing } = usePullToRefresh({
    onRefresh: refreshAll,
    scrollRef: scrollerRef,
  });

  return (
    <div ref={scrollerRef} className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-3 space-y-3" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid="calls-page">
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isFeatureEnabled('calls_keypad') ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {isFeatureEnabled('calls_keypad') && (
            <TabsTrigger value="external" data-testid="external-calls-tab">
              <Phone className="w-4 h-4 mr-2" />
              External Calls
            </TabsTrigger>
          )}
          {isFeatureEnabled('calls_keypad') && (
            <TabsTrigger value="internal" data-testid="internal-calls-tab">
              <Users className="w-4 h-4 mr-2" />
              Internal Calls
            </TabsTrigger>
          )}
        </TabsList>

        {isFeatureEnabled('calls_keypad') && (
        <TabsContent value="external" className="mt-4">
          <Card data-testid="dial-pad-card">
            <CardContent className="pt-6">
              {/* Display */}
              <div className="text-center mb-3">
                <div className="h-8 flex items-center justify-center">
                  <span className="text-2xl tracking-wider" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}>
                    {dialedNumber || 'Enter number'}
                  </span>
                </div>
              </div>

              {/* Dial Pad — slightly larger than the original cramped
                  size but not quite as bulky as the first attempt.
                  h-18 (72px) hits the sweet spot. On iPad/tablet in
                  portrait the grid widens (and buttons get taller) so
                  the dial pad fills the screen better. */}
              <div className="grid grid-cols-3 gap-3 max-w-[310px] md:portrait:max-w-md md:portrait:gap-4 mx-auto">
                {dialPadButtons.map(({ digit, letters }) => (
                  <Button
                    key={digit}
                    variant="outline"
                    className="h-[72px] md:portrait:h-[88px] w-full rounded-full text-[22px] md:portrait:text-[26px] font-semibold hover:bg-muted flex flex-col items-center justify-center leading-none gap-1"
                    style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
                    onClick={() => handleDial(digit)}
                    data-testid={`dial-btn-${digit}`}
                  >
                    <span>{digit}</span>
                    {letters && (
                      <span className="text-[10px] text-muted-foreground tracking-widest">
                        {letters}
                      </span>
                    )}
                  </Button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-center gap-3.5 mt-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-11 w-11 rounded-full"
                  onClick={handleDelete}
                  disabled={!dialedNumber}
                  data-testid="dial-delete-btn"
                >
                  ⌫
                </Button>
                <Button
                  size="icon"
                  className="h-[52px] w-[52px] rounded-full bg-green-500 hover:bg-green-600"
                  onClick={handleCall}
                  // Allow a tap when input is empty BUT a previous
                  // number is remembered → that tap retypes the
                  // last dialed number into the input. The user
                  // hits the button again to actually place the call.
                  disabled={externalCallLoading || (!dialedNumber && !lastDialed)}
                  data-testid="dial-call-btn"
                  title={!dialedNumber && lastDialed ? `Redial ${lastDialed}` : 'Place call'}
                >
                  {externalCallLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
                </Button>
                <div className="w-11" />
              </div>

              {(isFeatureEnabled('calls_keypad_recording') || isFeatureEnabled('calls_keypad_ai_summary')) && (() => {
                // iter156 — Affiliates, Leads, and Supervisors are NOT
                // permitted to disable recording. Toggle is force-locked
                // ON (visually greyed out) for those roles. Only full
                // admins AND special users (admin-created, `created_by_admin`
                // set) can disable recording per-call.
                //
                // SECURITY: this UI lock is mirrored on the server
                // (`/voice/outgoing` ignores `record=false` from any
                // non-admin/non-special user) so the lock cannot be
                // bypassed via devtools / direct SDK calls.
                const isAdminOrSpecial = (user?.role === 'admin') || !!user?.created_by_admin;
                const recordingLockedOn = !isAdminOrSpecial;
                return (
                  <div className="mt-4 space-y-2 border-t pt-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {recordingEnabled || recordingLockedOn
                          ? <Mic className="w-4 h-4" />
                          : <MicOff className="w-4 h-4" />}
                        <Label htmlFor="record-and-transcribe" className="text-sm">
                          Record, Transcribe, Summarize
                        </Label>
                      </div>
                      <Switch
                        id="record-and-transcribe"
                        checked={recordingLockedOn ? true : recordingEnabled}
                        disabled={recordingLockedOn}
                        onCheckedChange={(v) => {
                          if (recordingLockedOn) return;
                          setRecordingEnabled(v);
                          setTranscriptEnabled(v);
                        }}
                        data-testid="record-and-transcribe-switch"
                      />
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* iter157 — "Call from Contact List?" entry under the keypad.
              Tap → bottom sheet covering ~4/5 of the screen lists each
              saved external contact in a thread-card row with a single
              call icon. Tap the call icon → places the outbound call
              through the SAME `placeCall` path the keypad uses (so the
              role-lock + record toggle still apply). */}
          <button
            type="button"
            onClick={openExternalContactsSheet}
            data-testid="open-external-contacts-sheet"
            className="mt-4 w-full rounded-2xl border bg-white/90 dark:bg-[#141414] px-4 py-4 text-left shadow-lg flex items-center justify-between hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#586D52]" />
              <span className="text-base font-medium">Call from Contact List?</span>
            </div>
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          </button>
        </TabsContent>
        )}

        {isFeatureEnabled('calls_keypad') && (
        <TabsContent value="internal" className="mt-4 space-y-4">
          {/* Active Video Call surfaces here when in-call (was on the
              standalone Video tab before iter123). */}
          {isInCall && (
            <Card className={`bg-white/90 dark:bg-[#141414] border-0 shadow-lg overflow-hidden ${callState.isMinimized ? '' : ''}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={callState.participants[1]?.profile_picture} />
                      <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(callState.participants[1]?.role)} text-white text-xs`}>
                        {getInitials(callState.participants[1])}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{callState.participants[1]?.name || 'Unknown'}</p>
                      <p className={`text-xs ${callState.status === CallStatus.CONNECTED ? 'text-green-600' : 'text-yellow-600'}`}>
                        {callState.status === CallStatus.CONNECTED
                          ? formatVideoDuration(callState.duration)
                          : callState.status === CallStatus.CALLING
                            ? 'Calling...'
                            : 'Connecting...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {callState.providerType === 'native_webrtc' ? 'P2P' : 'Teams'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={toggleMinimize}
                    >
                      {callState.isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                {!callState.isMinimized && (
                  <>
                    <div className="relative bg-slate-900 h-64">
                      {mediaState.remoteStream ? (
                        <video
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover"
                          ref={(video) => {
                            if (video && mediaState.remoteStream) {
                              video.srcObject = mediaState.remoteStream;
                            }
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center text-white">
                            <Avatar className="h-20 w-20 mx-auto mb-2">
                              <AvatarImage src={callState.participants[1]?.profile_picture} />
                              <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(callState.participants[1]?.role)} text-white text-2xl`}>
                                {getInitials(callState.participants[1])}
                              </AvatarFallback>
                            </Avatar>
                            {callState.status !== CallStatus.CONNECTED && (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <p className="text-sm opacity-75">
                                  {callState.status === CallStatus.CALLING ? 'Ringing...' : 'Connecting...'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-3 right-3 w-24 h-32 bg-slate-800 rounded-lg overflow-hidden border-2 border-white/20">
                        {mediaState.localStream && mediaState.isCameraOn ? (
                          <video
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }}
                            ref={(video) => {
                              if (video && mediaState.localStream) {
                                video.srcObject = mediaState.localStream;
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-xs">
                            {!mediaState.isCameraOn ? <VideoOff className="w-6 h-6" /> : 'You'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-4 p-4 bg-slate-100 dark:bg-slate-800">
                      <Button
                        variant={mediaState.isMicOn ? 'outline' : 'destructive'}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={toggleMic}
                        data-testid="video-mic-toggle"
                      >
                        {mediaState.isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                      </Button>
                      <Button
                        variant={mediaState.isCameraOn ? 'outline' : 'destructive'}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={toggleCamera}
                        data-testid="video-camera-toggle"
                      >
                        {mediaState.isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={handleEndVideoCall}
                        data-testid="video-end-call"
                      >
                        <Phone className="w-5 h-5 rotate-[135deg]" />
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Internal Contacts - Call via WebRTC */}
          <Card data-testid="internal-contacts-card">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-3">Internal Contacts</h3>
              {internalContactsLoading ? (
                <PageLoader label="Loading contacts..." fullScreen={false} gradient={false} testId="internal-contacts-loading" />
              ) : internalContacts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No internal contacts</p>
                  <p className="text-xs mt-1">Add contacts from the Contacts page</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {internalContacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-center gap-3 p-2.5 bg-[#f5f5f5] dark:bg-[#0d0d0d] rounded-lg"
                      data-testid={`internal-call-contact-${contact.id}`}
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarImage src={contact.profile_picture} />
                        <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(contact.role)} text-white text-xs`}>
                          {getInitials(contact)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{getFullName(contact)}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground capitalize">{contact.role}</span>
                          {contact.state && (
                            <span className="text-xs text-muted-foreground">{contact.state}</span>
                          )}
                        </div>
                      </div>
                      {/* iter123 — Phone + Video buttons side-by-side
                          (the standalone Video tab was removed; video
                          now starts from the same contact card).
                          iter157 — icon-only (text labels removed). */}
                      <Button
                        size="icon"
                        className="bg-green-600 hover:bg-green-700 h-9 w-9 rounded-full"
                        onClick={() => handleInternalVoiceCall(contact)}
                        disabled={isInCall || isVideoInitiating}
                        data-testid={`internal-call-btn-${contact.id}`}
                        aria-label={`Voice call ${getFullName(contact)}`}
                      >
                        <Phone className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        className="bg-blue-600 hover:bg-blue-700 h-9 w-9 rounded-full"
                        onClick={() => handleStartVideoCall(contact)}
                        disabled={isInCall || isVideoInitiating}
                        data-testid={`internal-video-btn-${contact.id}`}
                        aria-label={`Video call ${getFullName(contact)}`}
                      >
                        {isVideoInitiating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Video className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Video Calls Tab */}
        <TabsContent value="video" className="mt-4 space-y-4">
          {/* Search Section */}
          <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Search Users</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Type a name to search..."
                  value={videoSearchQuery}
                  onChange={(e) => setVideoSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="video-search-input"
                />
              </div>
              {videoSearchQuery.length > 0 && videoSearchQuery.length < 2 && (
                <p className="text-xs text-muted-foreground mt-2">Type at least 2 characters to search</p>
              )}
            </CardContent>
          </Card>

          {/* Active Video Call */}
          {isInCall && (
            <Card className={`bg-white/90 dark:bg-[#141414] border-0 shadow-lg overflow-hidden ${callState.isMinimized ? '' : ''}`}>
              <CardContent className="p-0">
                {/* Video Header */}
                <div className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={callState.participants[1]?.profile_picture} />
                      <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(callState.participants[1]?.role)} text-white text-xs`}>
                        {getInitials(callState.participants[1])}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{callState.participants[1]?.name || 'Unknown'}</p>
                      <p className={`text-xs ${callState.status === CallStatus.CONNECTED ? 'text-green-600' : 'text-yellow-600'}`}>
                        {callState.status === CallStatus.CONNECTED 
                          ? formatVideoDuration(callState.duration)
                          : callState.status === CallStatus.CALLING 
                            ? 'Calling...' 
                            : 'Connecting...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Badge variant="outline" className="text-xs">
                      {callState.providerType === 'native_webrtc' ? 'P2P' : 'Teams'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={toggleMinimize}
                    >
                      {callState.isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                
                {/* Video Display Area */}
                {!callState.isMinimized && (
                  <>
                    <div className="relative bg-slate-900 h-64">
                      {/* Remote Video */}
                      {mediaState.remoteStream ? (
                        <video
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover"
                          ref={(video) => {
                            if (video && mediaState.remoteStream) {
                              video.srcObject = mediaState.remoteStream;
                            }
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center text-white">
                            <Avatar className="h-20 w-20 mx-auto mb-2">
                              <AvatarImage src={callState.participants[1]?.profile_picture} />
                              <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(callState.participants[1]?.role)} text-white text-2xl`}>
                                {getInitials(callState.participants[1])}
                              </AvatarFallback>
                            </Avatar>
                            {callState.status !== CallStatus.CONNECTED && (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <p className="text-sm opacity-75">
                                  {callState.status === CallStatus.CALLING ? 'Ringing...' : 'Connecting...'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Local Video Preview (small) */}
                      <div className="absolute bottom-3 right-3 w-24 h-32 bg-slate-800 rounded-lg overflow-hidden border-2 border-white/20">
                        {mediaState.localStream && mediaState.isCameraOn ? (
                          <video
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }}
                            ref={(video) => {
                              if (video && mediaState.localStream) {
                                video.srcObject = mediaState.localStream;
                              }
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white text-xs">
                            {!mediaState.isCameraOn ? <VideoOff className="w-6 h-6" /> : 'You'}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Video Controls */}
                    <div className="flex items-center justify-center gap-4 p-4 bg-slate-100 dark:bg-slate-800">
                      <Button 
                        variant={mediaState.isMicOn ? 'outline' : 'destructive'}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={toggleMic}
                        data-testid="video-mic-toggle"
                      >
                        {mediaState.isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                      </Button>
                      <Button 
                        variant={mediaState.isCameraOn ? 'outline' : 'destructive'}
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={toggleCamera}
                        data-testid="video-camera-toggle"
                      >
                        {mediaState.isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                      </Button>
                      <Button 
                        variant="destructive"
                        size="icon"
                        className="h-12 w-12 rounded-full"
                        onClick={handleEndVideoCall}
                        data-testid="video-end-call"
                      >
                        <Phone className="w-5 h-5 rotate-[135deg]" />
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search Results */}
          {videoSearchQuery.length >= 2 && (
            <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg">
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">Search Results</h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {filteredVideoUsers.length} user{filteredVideoUsers.length !== 1 ? 's' : ''} found
                </p>
                
                <div className="space-y-2">
                  {filteredVideoUsers.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No users found matching "{videoSearchQuery}"</p>
                    </div>
                  ) : (
                    filteredVideoUsers.map((u) => (
                      <Card 
                        key={u.id}
                        className="bg-[#f5f5f5] dark:bg-[#1a1a1a] border-0"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <Avatar className="h-12 w-12 border-2 border-white dark:border-slate-700 shadow-md">
                              <AvatarImage src={u.profile_picture} />
                              <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(u.role)} text-white`}>
                                {getInitials(u)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold truncate">{getFullName(u)}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge className={`text-xs capitalize px-2 py-0 border ${getRoleBadgeStyle(u.role)}`}>
                                  {u.role}
                                </Badge>
                                {u.state && (
                                  <span className="text-xs text-muted-foreground">{u.state}</span>
                                )}
                              </div>
                            </div>
                            <Button 
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => handleStartVideoCall(u)}
                              disabled={isInCall || isVideoInitiating}
                              data-testid={`video-call-${u.id}`}
                            >
                              {isVideoInitiating ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Video className="w-4 h-4 mr-2" />
                              )}
                              Video Call
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          {!isInCall && videoSearchQuery.length < 2 && (
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg">
              <CardContent className="p-4 text-center">
                <Video className="w-10 h-10 mx-auto mb-2 text-blue-600" />
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">Video Calls</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Search for a user to start a 1:1 video call. Calls between Leads/Affiliates use P2P WebRTC. 
                  Calls involving Supervisors will route through Microsoft Teams.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

      </Tabs>

      {/* Transcript Dialog - Styled like AI Personality popup */}
      <Dialog open={showTranscriptDialog} onOpenChange={setShowTranscriptDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Call Transcript</DialogTitle>
            {selectedTranscript && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedTranscript.phone_number} • {formatDuration(selectedTranscript.duration)}
              </p>
            )}
          </DialogHeader>
          <div className="px-4 pb-4">
            {selectedTranscript && (
              <ScrollArea className="h-[40vh] bg-muted/30 p-3 rounded-xl">
                <DiarizedTranscript
                  segments={selectedTranscript.diarized_segments}
                  fallbackText={selectedTranscript.transcript}
                />
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* iter157 — External Contacts bottom sheet (4/5 of viewport,
          rounded top edges). Tap the call icon on any row to dial
          that contact's saved number through the same outbound
          flow the keypad uses.
          iter158 — search input + per-user star (favorites pinned
          to the top of the list). */}
      <Sheet open={externalContactsSheetOpen} onOpenChange={setExternalContactsSheetOpen}>
        <SheetContent
          side="bottom"
          className="h-[80vh] rounded-t-3xl p-0 flex flex-col"
          data-testid="external-contacts-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b">
            <SheetTitle className="text-xl font-semibold">External Contacts</SheetTitle>
          </SheetHeader>
          <div className="px-4 pt-3 pb-1 border-b bg-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                value={externalContactsQuery}
                onChange={(e) => setExternalContactsQuery(e.target.value)}
                placeholder="Search by name or number"
                className="pl-9 h-10 rounded-full"
                data-testid="external-contacts-search-input"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
            {(() => {
              // iter158 — filter + sort. Starred always at top; within
              // each bucket sort by display name. Search matches name
              // OR digits of phone (strip + and spaces from both sides).
              const q = externalContactsQuery.trim().toLowerCase();
              const qDigits = q.replace(/[^0-9]/g, '');
              const nameOf = (c) => (c.name || '').trim() || c.phone_number || '';
              const filtered = !q
                ? externalContacts
                : externalContacts.filter((c) => {
                    const name = nameOf(c).toLowerCase();
                    const phoneDigits = (c.phone_number || '').replace(/[^0-9]/g, '');
                    return (
                      name.includes(q) ||
                      (qDigits && phoneDigits.includes(qDigits))
                    );
                  });
              const sorted = [...filtered].sort((a, b) => {
                const sa = a.starred ? 1 : 0;
                const sb = b.starred ? 1 : 0;
                if (sa !== sb) return sb - sa;
                return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' });
              });

              if (externalContactsLoading) {
                return (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    <span>Loading contacts…</span>
                  </div>
                );
              }
              if (sorted.length === 0) {
                return (
                  <div className="text-center py-10 text-muted-foreground" data-testid="external-contacts-empty">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      {q ? 'No matches.' : 'No external contacts yet.'}
                    </p>
                  </div>
                );
              }

              return sorted.map((contact) => {
                const displayName = nameOf(contact);
                const initials = displayName
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0]?.toUpperCase() || '')
                  .join('') || '?';
                const isDialing = externalContactDialing === contact.id;
                const isStarPending = !!starPending[contact.id];
                return (
                  <div
                    key={contact.id}
                    className="rounded-2xl border bg-white/90 dark:bg-[#141414] p-3 flex items-center gap-3 shadow-sm"
                    data-testid={`external-contact-card-${contact.id}`}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-[#586D52]/20 text-[#586D52] text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">{contact.phone_number}</p>
                    </div>
                    {/* iter158.1 — star sits AFTER the name/number, just
                        before the call icon. */}
                    <button
                      type="button"
                      onClick={() => toggleStarContact(contact)}
                      disabled={isStarPending}
                      className="shrink-0 p-1 -m-1 hover:opacity-80 transition-opacity"
                      aria-label={contact.starred ? `Unstar ${displayName}` : `Star ${displayName}`}
                      data-testid={`external-contact-star-btn-${contact.id}`}
                    >
                      <Star
                        className={`w-5 h-5 ${
                          contact.starred
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground'
                        } ${isStarPending ? 'opacity-50' : ''}`}
                      />
                    </button>
                    <Button
                      size="icon"
                      className="bg-green-600 hover:bg-green-700 h-10 w-10 rounded-full shrink-0"
                      onClick={() => callExternalContact(contact)}
                      disabled={isDialing || externalContactDialing !== null}
                      data-testid={`external-contact-call-btn-${contact.id}`}
                      aria-label={`Call ${displayName}`}
                    >
                      {isDialing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Phone className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                );
              });
            })()}
          </div>
        </SheetContent>
      </Sheet>

    </div>
  );
};

export default CallsPage;
